import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Tag, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Landing handler for one-time discount links shared by the marketing
 * admin. The token is resolved server-side; on success we redirect into
 * the relevant checkout (advertising or sponsored article) with the
 * code AND the token pre-filled so the backend can consume the link
 * once the order is paid.
 */
export default function DiscountLinkLanding() {
  const { token } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, error: '', data: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/public/marketing-discount/link/${token}`);
        const d = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) {
          setState({ loading: false, error: d.detail || 'Link nije valjan.', data: null });
          return;
        }
        setState({ loading: false, error: '', data: d });
        // Auto-redirect after a short pause so the user sees what's happening
        const target = d.scope === 'sponsored'
          ? `/sponzorirani-clanak?d=${encodeURIComponent(d.code)}&t=${token}`
          : `/oglasavanje?d=${encodeURIComponent(d.code)}&t=${token}`;
        setTimeout(() => nav(target, { replace: true }), 1400);
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: 'Greška pri provjeri linka.', data: null });
      }
    })();
    return () => { cancelled = true; };
  }, [token, nav]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="discount-link-landing">
      <div className="max-w-md w-full rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-8 text-center">
        {state.loading && (
          <div className="flex flex-col items-center gap-3 text-zinc-500">
            <Loader2 className="animate-spin" size={36} />
            <div>Provjeravam vaš link za popust…</div>
          </div>
        )}
        {!state.loading && state.error && (
          <div className="flex flex-col items-center gap-3" data-testid="discount-link-error">
            <AlertTriangle className="text-amber-500" size={36} />
            <h1 className="text-lg font-bold">Link nije dostupan</h1>
            <p className="text-sm text-zinc-500">{state.error}</p>
          </div>
        )}
        {!state.loading && state.data && (
          <div className="flex flex-col items-center gap-3" data-testid="discount-link-ok">
            <Tag className="text-emerald-500" size={36} />
            <h1 className="text-lg font-bold">Popust aktiviran</h1>
            <p className="text-sm text-zinc-500">
              Kod <span className="font-mono font-semibold">{state.data.code}</span> automatski je primijenjen.
              Preusmjeravam vas na obrazac{state.data.scope === 'sponsored' ? ' sponzoriranog članka' : ' oglašavanja'}…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
